import { useState } from "react";
import { Plus, Search, FileText, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useFetchPorts } from "@/hooks/usePorts";
import { useCountries } from "@/hooks/useCountries";
import { useCities } from "@/hooks/useCities";
import { AddPortDialog } from "@/components/ports/AddPortDialog";
import { EditPortDialog } from "@/components/ports/EditPortDialog";
import type { Port } from "@/types/port";
import * as XLSX from 'xlsx';

const Ports = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedPort, setSelectedPort] = useState<Port | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const { data: ports = [], isLoading, refetch } = useFetchPorts();
  const { data: countries = [] } = useCountries();
  const { data: cities = [] } = useCities();

  const filteredPorts = ports.filter(port =>
    port.name_ar.toLowerCase().includes(searchTerm.toLowerCase()) ||
    port.name_en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    port.port_code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEditClick = (port: Port) => {
    setSelectedPort(port);
    setShowEditDialog(true);
  };

  const handleExport = () => {
    if (filteredPorts.length === 0) {
      toast.error("لا توجد بيانات لتصديرها");
      return;
    }

    const exportData = filteredPorts.map(port => ({
      'الاسم بالعربية': port.name_ar,
      'الاسم بالإنجليزية': port.name_en || '',
      'كود الميناء': port.port_code,
      'الدولة': port.countries?.name_ar || '',
      'المدينة': port.cities?.name_ar || '',
      'تاريخ الإنشاء': new Date(port.created_at).toLocaleDateString('ar-SA')
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "الموانئ");
    XLSX.writeFile(workbook, "ports.xlsx");
    toast.success("تم تصدير البيانات بنجاح");
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-6" dir="rtl">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">جاري التحميل...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6" dir="rtl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">إدارة الموانئ</h1>
          <p className="text-muted-foreground mt-2">
            إدارة وتتبع جميع الموانئ في النظام
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleExport} variant="outline" size="sm">
            <Download className="h-4 w-4 ml-2" />
            تصدير Excel
          </Button>
          <Button onClick={() => setShowAddDialog(true)} size="sm">
            <Plus className="h-4 w-4 ml-2" />
            إضافة ميناء جديد
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="flex gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="البحث في الموانئ..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pr-10"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              إجمالي الموانئ
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{ports.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              الدول المتاحة
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Set(ports.map(p => p.country_id).filter(Boolean)).size}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              المدن المتاحة
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Set(ports.map(p => p.city_id).filter(Boolean)).size}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Ports Grid */}
      {filteredPorts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPorts.map((port) => (
            <Card key={port.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleEditClick(port)}>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{port.name_ar}</CardTitle>
                    {port.name_en && (
                      <p className="text-sm text-muted-foreground mt-1">{port.name_en}</p>
                    )}
                  </div>
                  <Badge variant="secondary">{port.port_code}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {port.countries && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">الدولة:</span>
                      <span className="text-sm">{port.countries.name_ar}</span>
                    </div>
                  )}
                  {port.cities && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">المدينة:</span>
                      <span className="text-sm">{port.cities.name_ar}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">تاريخ الإضافة:</span>
                    <span className="text-sm">{new Date(port.created_at).toLocaleDateString('ar-SA')}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-8">
            <div className="text-center">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">لا توجد موانئ</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm ? "لم يتم العثور على موانئ مطابقة لبحثك" : "لم يتم إضافة أي موانئ بعد"}
              </p>
              {!searchTerm && (
                <Button onClick={() => setShowAddDialog(true)}>
                  <Plus className="h-4 w-4 ml-2" />
                  إضافة أول ميناء
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      <AddPortDialog 
        open={showAddDialog} 
        onOpenChange={setShowAddDialog}
        onSuccess={() => {
          refetch();
          setShowAddDialog(false);
        }}
        countries={countries}
        cities={cities}
      />

      {selectedPort && (
        <EditPortDialog 
          open={showEditDialog} 
          onOpenChange={setShowEditDialog}
          port={selectedPort}
          onSuccess={() => {
            refetch();
            setShowEditDialog(false);
            setSelectedPort(null);
          }}
          countries={countries}
          cities={cities}
        />
      )}
    </div>
  );
};

export default Ports;